const CHECKOUT_RETURN_KEY = "lupuslogix_active_checkout_return";
const STRIPE_LEGAL_ORIGIN_KEY = "lupuslogix_legal_from_stripe";
const CHECKOUT_SETTLING_KEY = "lupuslogix_checkout_settling_at";

// Auto-expire checkout markers after 60 minutes so an abandoned checkout
// (user closed the Stripe tab without returning) can't surface a stale
// "Return to checkout" banner hours or sessions later.
const CHECKOUT_RETURN_TTL_MS = 60 * 60 * 1000;
// Stripe webhooks can lag; keep suppressing the stale "inactive" banner for
// a few minutes after a successful Checkout return (survives reloads and
// PWA relaunches because the flag is mirrored into localStorage).
export const CHECKOUT_SETTLING_TTL_MS = 5 * 60 * 1000;

type CheckoutReturnState = {
  url?: string;
  setAt: number;
};

const isStripeCheckoutUrl = (url: string | undefined): url is string => {
  if (!url) return false;
  try {
    const parsed = new URL(url);
    return parsed.protocol === "https:" && parsed.hostname === "checkout.stripe.com";
  } catch {
    return false;
  }
};

// Read/write helpers that fan out to BOTH sessionStorage (per-tab) and
// localStorage (cross-tab). Stripe opens our Terms/Privacy links in a
// brand-new tab with rel="noopener noreferrer", so sessionStorage from
// the checkout tab is unreachable and document.referrer is empty. The
// localStorage mirror lets the new tab still recognize it came from an
// active Stripe Checkout and route "Back to Home" back to the checkout
// URL instead of falling through to /dashboard → /subscription.
const writeBoth = (key: string, value: string) => {
  try { sessionStorage.setItem(key, value); } catch { /* ignore */ }
  try { localStorage.setItem(key, value); } catch { /* ignore */ }
};
const removeBoth = (key: string) => {
  try { sessionStorage.removeItem(key); } catch { /* ignore */ }
  try { localStorage.removeItem(key); } catch { /* ignore */ }
};
const readEither = (key: string): string | null => {
  try {
    const s = sessionStorage.getItem(key);
    if (s) return s;
  } catch { /* ignore */ }
  try { return localStorage.getItem(key); } catch { return null; }
};

export const markCheckoutRedirect = (url: string | undefined) => {
  try {
    const state: CheckoutReturnState = {
      ...(isStripeCheckoutUrl(url) ? { url } : {}),
      setAt: Date.now(),
    };
    writeBoth(CHECKOUT_RETURN_KEY, JSON.stringify(state));
    removeBoth(STRIPE_LEGAL_ORIGIN_KEY);
  } catch {
    /* best effort */
  }
};

export const clearCheckoutReturn = () => {
  try {
    removeBoth(CHECKOUT_RETURN_KEY);
    removeBoth(STRIPE_LEGAL_ORIGIN_KEY);
  } catch {
    /* best effort */
  }
};

/**
 * Suppress stale cached entitlement UI while the successful Checkout return
 * is being reconciled with Stripe/webhook state.
 */
export const markCheckoutSettling = () => {
  writeBoth(CHECKOUT_SETTLING_KEY, String(Date.now()));
};

export const isCheckoutSettling = () => {
  try {
    const setAt = Number(readEither(CHECKOUT_SETTLING_KEY));
    if (!setAt || Date.now() - setAt > CHECKOUT_SETTLING_TTL_MS) {
      if (setAt) removeBoth(CHECKOUT_SETTLING_KEY);
      return false;
    }
    return true;
  } catch {
    return false;
  }
};

/** Called once entitlement is confirmed active — no need to suppress anymore. */
export const clearCheckoutSettling = () => {
  removeBoth(CHECKOUT_SETTLING_KEY);
};

/** True when a Checkout redirect happened recently (covers client refreshes). */
export const isRecentCheckoutRedirect = (windowMs = CHECKOUT_SETTLING_TTL_MS) => {
  const state = readCheckoutReturn();
  return !!state && Date.now() - state.setAt < windowMs;
};

export const readCheckoutReturn = (): CheckoutReturnState | null => {
  try {
    const raw = readEither(CHECKOUT_RETURN_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CheckoutReturnState>;
    if (!parsed.setAt || Date.now() - parsed.setAt > CHECKOUT_RETURN_TTL_MS) {
      clearCheckoutReturn();
      return null;
    }
    return {
      ...(isStripeCheckoutUrl(parsed.url) ? { url: parsed.url } : {}),
      setAt: parsed.setAt,
    };
  } catch {
    return null;
  }
};

export const rememberStripeLegalOrigin = () => {
  try {
    // Store a timestamp (not a bare flag) so the sticky origin expires on the
    // same 60-minute window as the checkout-return marker — previously this
    // flag lived forever and could cause false banners sessions later.
    writeBoth(STRIPE_LEGAL_ORIGIN_KEY, String(Date.now()));
  } catch {
    /* best effort */
  }
};

export const hasStripeLegalOrigin = () => {
  try {
    const raw = readEither(STRIPE_LEGAL_ORIGIN_KEY);
    if (!raw) return false;
    const ts = Number(raw);
    // Legacy bare "1" flag or expired timestamp: purge and report false.
    if (!ts || ts === 1 || Date.now() - ts > CHECKOUT_RETURN_TTL_MS) {
      removeBoth(STRIPE_LEGAL_ORIGIN_KEY);
      return false;
    }
    return true;
  } catch {
    return false;
  }
};

export const isStripeCheckoutReferrer = (referrer: string | undefined) => {
  if (!referrer) return false;
  try {
    return new URL(referrer).hostname === "checkout.stripe.com";
  } catch {
    return /checkout\.stripe\.com/i.test(referrer);
  }
};